// Greedy steering (MOB-3 default pathing). PURE (ENG-1).
//
// The direct-vector steer used by every non-villager mob (D11): head straight
// toward the goal, jump a 1-high blocker, and refuse to walk off a drop deeper
// than STEER.MAX_DROP or into a hazard (lava/fire). A* (astar.ts) is reserved for
// villagers/golems/warden later; this covers the M5 roster.
import { MOB } from '../../config';

export interface SteerWorld {
  isSolid(x: number, y: number, z: number): boolean;
  /** Optional hazard probe (lava/fire) — mobs won't step onto it. */
  isHazard?(x: number, y: number, z: number): boolean;
  /** Fluid probe — a land mob (`avoidFluid`) won't wade in. */
  isFluid?(x: number, y: number, z: number): boolean;
}

export interface SteerResult {
  /** Unit horizontal direction to move (0,0 = hold — blocked/cliff). */
  dx: number;
  dz: number;
  jump: boolean;
}

const HOLD: SteerResult = { dx: 0, dz: 0, jump: false };

/** Blocks the ground ahead drops below the mob's feet, or Infinity if deeper than
 *  `maxDrop` (a cliff). Foot rests at `fy`; ground directly below is `fy-1`. */
function groundDrop(world: SteerWorld, fx: number, fy: number, fz: number, maxDrop: number): number {
  for (let d = 0; d <= maxDrop; d++) {
    if (world.isSolid(fx, fy - 1 - d, fz)) return d;
  }
  return Infinity;
}

/** Steer an entity at (ex,ey,ez) toward horizontal goal (tx,tz). */
export function steerToward(
  world: SteerWorld,
  ex: number, ey: number, ez: number,
  tx: number, tz: number,
  opts: { maxDrop?: number; probe?: number; avoidFluid?: boolean } = {},
): SteerResult {
  const maxDrop = opts.maxDrop ?? MOB.STEER.MAX_DROP;
  const probe = opts.probe ?? MOB.STEER.PROBE;
  let dx = tx - ex, dz = tz - ez;
  const len = Math.hypot(dx, dz);
  if (len < 1e-4) return HOLD;
  dx /= len; dz /= len;

  const fx = Math.floor(ex + dx * probe);
  const fz = Math.floor(ez + dz * probe);
  const fy = Math.floor(ey);

  // Hazard directly ahead (at foot or one below) → don't walk into it.
  if (world.isHazard?.(fx, fy, fz) || world.isHazard?.(fx, fy - 1, fz)) return HOLD;

  // A land animal treats water like a cliff: it wades no deeper than its feet,
  // so cows stop at the shoreline instead of strolling along the seabed.
  if (opts.avoidFluid && world.isFluid?.(fx, fy, fz)) return HOLD;

  // Obstacle at foot level → jump it if there's headroom for a 1-high step.
  if (world.isSolid(fx, fy, fz)) {
    const headroom = !world.isSolid(fx, fy + 1, fz) && !world.isSolid(fx, fy + 2, fz);
    return { dx, dz, jump: headroom };
  }

  // Open ahead → refuse to step off a cliff deeper than maxDrop.
  if (groundDrop(world, fx, fy, fz, maxDrop) > maxDrop) return HOLD;
  return { dx, dz, jump: false };
}
